/**
 * @fse/ui — public API.
 *
 * This is the SWAP POINT. Consumers (apps/web today, apps/mobile via
 * a parallel surface) import exclusively from this entry point. The
 * underlying library (MUI v9 today) can be replaced by rewriting the
 * files inside src/ without touching any consumer.
 *
 * Rules:
 *  - Never re-export raw MUI components. Always go through a wrapper
 *    in components/ so we own the prop surface.
 *  - Never expose MUI's `sx` prop as part of FSE's public API. If a
 *    new prop is needed, add it to the wrapper.
 *  - Theme tokens live in theme/tokens.ts (platform-agnostic data).
 *    Theme adapter (createTheme) lives in theme/theme.ts (MUI-specific).
 */

// Theme
export { FseThemeProvider } from './theme/ThemeProvider.js';
export { fseTheme, buildTheme } from './theme/theme.js';
export { useColorMode, ColorModeProvider } from './theme/ColorMode.js';
export {
  brand,
  mdSysColor,
  palette,
  paletteLight,
  paletteDark,
  typography,
  spacing,
  radius,
  elevation,
  tap,
  stateLayer,
  motion,
  tokens,
  type FseTokens,
  type ColorMode,
} from './theme/tokens.js';

// Components (FSE-controlled wrappers)
export { Button, type ButtonProps } from './components/Button.js';
export { TextField, type TextFieldProps } from './components/TextField.js';
export {
  Card,
  CardContent,
  CardHeader,
  type CardProps,
  type CardContentProps,
  type CardHeaderProps,
} from './components/Card.js';
export { Stack, type StackProps } from './components/Stack.js';
export { AppShell, type AppShellProps, type NavItem } from './components/AppShell.js';
export { ColorModeToggle } from './components/ColorModeToggle.js';
export {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  type DialogProps,
  type DialogTitleProps,
  type DialogContentProps,
  type DialogContentTextProps,
  type DialogActionsProps,
} from './components/Dialog.js';
export { Snackbar, type SnackbarProps } from './components/Snackbar.js';
export { IconButton, type IconButtonProps } from './components/IconButton.js';

// ── FSE design-system kit (Mango — Farmer Market) ──────────────────────────
// MD3-grounded wrappers built on the shared tokens (theme/tokens.ts). These
// are the canonical north-star components; the raw MUI re-exports below are
// legacy passthroughs that will be retired as screens migrate onto these.
export { Text, type TextProps, type TextVariant } from './components/Text.js';
export { Icon, type IconProps } from './components/Icon.js';
export { OtpInput, type OtpInputProps } from './components/OtpInput.js';
export { PhoneInput, type PhoneInputProps } from './components/PhoneInput.js';
export {
  SegmentedButton,
  type SegmentedButtonProps,
  type SegmentedButtonOption,
} from './components/SegmentedButton.js';
export { Switch, type SwitchProps } from './components/Switch.js';
export { SearchBar, type SearchBarProps } from './components/SearchBar.js';
// MD3 chip (assist / filter / input) — the canonical Chip. All product screens
// migrated off the raw MUI Chip on 2026-06-04 (TD-089), so this IS `Chip` now.
export { Chip, type ChipProps, type ChipKind, type ChipColor } from './components/Chip.js';
export { Badge, type BadgeProps, type BadgeColor } from './components/Badge.js';
export {
  StatusPill,
  type StatusPillProps,
  type StatusPillVariant,
} from './components/StatusPill.js';
export { Money, type MoneyProps, type MoneySize } from './components/Money.js';
export { StatCard, type StatCardProps, type StatCardColor } from './components/StatCard.js';
export { StatGrid, type StatGridProps } from './components/StatGrid.js';
export { Sparkline, type SparklineProps } from './components/Sparkline.js';
export { MarketStatBar, type MarketStatBarProps } from './components/MarketStatBar.js';
export { ListItem, type ListItemProps } from './components/ListItem.js';
export { TopAppBar, type TopAppBarProps } from './components/TopAppBar.js';
export { BottomNav, type BottomNavProps, type BottomNavItem } from './components/BottomNav.js';
export { Fab, type FabProps } from './components/Fab.js';
export { Menu, type MenuProps, type MenuOption } from './components/Menu.js';
export { Stepper, type StepperProps } from './components/Stepper.js';
export { LanguageRow, type LanguageRowProps } from './components/LanguageRow.js';
export {
  LanguagePicker,
  type LanguagePickerProps,
  type LanguageOption,
} from './components/LanguagePicker.js';

// MUI primitives re-exported through @fse/ui as the canonical entry point.
// Consumers must NEVER import from @mui/material directly; this re-export
// preserves the swap promise (replacing MUI = replacing only this file).
// Wrap each primitive in a Button-style forwardRef later if/when we want
// to apply FSE-specific defaults; for now transparent passthrough is fine.
export {
  Alert,
  Box,
  Checkbox,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Toolbar,
  Typography,
} from '@mui/material';
export type {
  AlertProps,
  BoxProps,
  CheckboxProps,
  CircularProgressProps,
  ContainerProps,
  DividerProps,
  FormControlProps,
  FormControlLabelProps,
  FormHelperTextProps,
  InputLabelProps,
  MenuItemProps,
  PaperProps,
  SelectProps,
  SkeletonProps,
  TableProps,
  TableCellProps,
  TableContainerProps,
  ToolbarProps,
  TypographyProps,
} from '@mui/material';

// Legacy named export kept for any pre-existing imports.
// Will be removed once consumers migrate to `tokens`.
import { palette as _palette, spacing as _spacing, radius as _radius } from './theme/tokens.js';
export const theme = {
  colors: {
    primary: _palette.primary[700],
    secondary: _palette.secondary[500],
    bg: _palette.neutral.bg,
    text: _palette.neutral.text,
    muted: _palette.neutral.textMuted,
    danger: _palette.semantic.danger,
  },
  spacing: {
    xs: _spacing.xs,
    sm: _spacing.sm,
    md: _spacing.md,
    lg: _spacing.lg,
    xl: _spacing.xl,
  },
  radius: { sm: _radius.sm, md: _radius.md, lg: _radius.lg },
} as const;
export type Theme = typeof theme;
